import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppSelector } from 'store/configureStore';
import { getDefaultCheckboxValue } from 'utils/renderTemplates';
import { isQldRegion } from 'utils/petFormHelpers';
import { getNestedError } from 'utils/splitDot';
import { sanitizeForm } from 'utils/htmlSanitizer';
import Icon from './Icon/Icon';

interface CheckboxProps {
    id: string;
    name: string;
    label?: string;
    disabled?: boolean;
    className?: string;
    validation?: any;
    showGroupError?: boolean;
    defaultState?: boolean;
    defaultDisabled?: boolean;
    showValidationErrorMessage?: boolean;
}

const Checkbox: React.FC<CheckboxProps> = ({
    id,
    name,
    label,
    disabled = false,
    className = '',
    validation,
    showGroupError = true,
    defaultState = false,
    defaultDisabled = false,
    showValidationErrorMessage = true,
}) => {
    const { register, formState: { errors, touchedFields }, watch, getValues, setValue } = useFormContext();
     const stageOnePetsData = useAppSelector((state) => state.Tenant.tenantPets);
     const formValue = watch(name);
    /**
     * Determines the checked state of the checkbox.
     * - If defaultState is true, use it.
     * - Otherwise, use getDefaultCheckboxValue to determine initial checked state.
     * - Fallback to watch(name) for dynamic updates.
     */
    const checked: boolean = formValue !== undefined 
    ? !!formValue 
    : defaultState 
        ? true 
        : getDefaultCheckboxValue(name ?? "", stageOnePetsData ?? []);
    const isDisabled = defaultDisabled ?? disabled;

        // Ensure the form's internal value is populated on mount if it's not already set
    useEffect(() => {
        if (!name) return;

        setValue(name, checked, { shouldValidate: false, shouldDirty: false });
    }, [name, checked, getValues, setValue]);
    
    // Only show errors after field has been touched (consistent with RadioInput behavior)
    const isTouched = name && getNestedError(touchedFields, name);

    // Get group validation info
    const groupName = validation?.find((v: { atLeastOneOfGroup?: string }) => v.atLeastOneOfGroup)?.atLeastOneOfGroup;
    const groupError = groupName && isTouched && errors[groupName]?.message;

    // Only show error if it's not a group error or if this checkbox is designated to show group error
    const nestedErrorMessage = !groupName && name && isTouched && getNestedError(errors, name)?.message
        ? getNestedError(errors, name).message
        : undefined;

    const errorMessage = showGroupError && groupError
        ? groupError
        : nestedErrorMessage;

    // Icon logic based on checked state
    const iconName = checked ? isDisabled ? 'checkboxTickedDisabled' : 'checkboxTicked' : 'checkboxEmpty';
    return (
        <>
            <label
                htmlFor={id ?? name}
                className={`cursor-pointer d-inline-flex ${isQldRegion() ? 'align-items-md-start' : 'align-items-md-center'} gap-5px ${
                    disabled ? 'opacity-50' : ''
                } ${className}`}
            >
                <span
                    role="checkbox"
                    aria-checked={checked}
                    aria-disabled={defaultDisabled ?? disabled}
                    title={label}
                >
                    <Icon name={iconName} size={24} />
                </span>
                {label && (
                    <span className="fs-16px text-black" dangerouslySetInnerHTML={{ __html: sanitizeForm(label ?? '') }} />
                )}
                <input
                    id={id ?? name}
                    // name={name}
                    type="checkbox"
                    disabled={defaultDisabled ?? disabled}
                    style={{ display: 'none' }}
                    {...register(name, validation)}
                    defaultChecked={getDefaultCheckboxValue(name ?? "", stageOnePetsData ?? [])}
                />
            </label>
            {errorMessage && showValidationErrorMessage && (
                <div className="text-red fs-14px">{errorMessage?.toString()}</div>
            )}
        </>
    );
};

export default Checkbox;
